import React, { useEffect, useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

declare global { interface Window { grecaptcha: any; } }

export function HomePage() {
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    const response = window.grecaptcha?.getResponse();
    if (!response) { alert('Please complete the reCAPTCHA.'); return; }
    setSending(true);
    emailjs.sendForm('service_ecjoxln', 'template_i8bv94m', formRef.current, 'WGhWRxHAfs-zdamaD')
      .then(() => { setSent(true); setSending(false); formRef.current?.reset(); window.grecaptcha.reset(); })
      .catch(() => { setSending(false); alert('Failed to send. Please try again.'); });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
        window.grecaptcha.render(recaptchaRef.current, { sitekey: '6LfFM_YsAAAAALUW4QGVeMk2xrPf4sAJPXlcIhe9' });
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('is-visible'); });
    }, { threshold: 0.1 });
    document.querySelectorAll('.animate-on-scroll, .animate-from-right').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Navbar />

      <section className="hero is-fullheight video-hero">
        <video autoPlay muted loop playsInline className="video-bg">
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-body">
          <div className="container has-text-centered">
            <h1 className="title is-1 has-text-white hero-title">
              <span className="hero-title-border">SITECH<br/>ESPORTS</span>
            </h1>
            <p className="subtitle is-5 has-text-white hero-subtitle">
              Simulation-powered esports pricing for modern sportsbooks
            </p>
            <a href="#contact" className="button is-primary is-large mt-5">REQUEST DEMO</a>
          </div>
        </div>
      </section>

      <section id="about" className="section about-section">
        <div className="container">
          <h2 className="title is-2 has-text-white has-text-centered animate-on-scroll">About Sitech eSports</h2>
          <p className="is-size-5 mb-5 about-intro has-text-centered animate-on-scroll delay-1">
            Sitech eSports is a B2B esports betting technology company providing simulation-powered odds pricing to sportsbook operators worldwide.
          </p>
          <div className="columns is-vcentered mt-4">
            <div className="column is-7">
              <div className="content is-medium animate-on-scroll delay-2">
                <p>We specialise in building automated pricing infrastructure for the esports betting market. Our Monte Carlo simulation engine runs 100,000 iterations per fixture, modelling game-specific mechanics to produce accurate, real-time probabilities across a wide range of betting markets â€” from series winner and correct score to map winner, over/under kill lines, and multi-leg bet builders.</p>
                <p>Currently live on the Call of Duty League (CDL), our platform delivers sub-second pricing updates via an event-driven API, enabling operators to offer competitive esports odds without the cost of building in-house trading teams.</p>
                <p>With expansion into Valorant and additional esports titles on our roadmap, Sitech eSports is building the pricing layer that powers the next generation of esports betting.</p>
              </div>
            </div>
            <div className="column is-5">
              <div className="about-image-wrapper animate-from-right">
                <img src="https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600&q=80" alt="Esports arena" className="about-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="parallax-divider"></section>

      <section id="services" className="section services-section">
        <div className="container">
          <h2 className="title is-2 has-text-centered animate-on-scroll">Our Services</h2>
          <div className="columns is-multiline mt-5">
            <div className="column is-4">
              <div className="card">
                <div className="card-content has-text-centered">
                  <i className="fa-solid fa-chart-line service-icon"></i>
                  <h3 className="title is-4 mt-4">Match Pricing</h3>
                  <p>Real-time odds across series winner, correct score, map winner, and over/under markets.</p>
                </div>
              </div>
            </div>
            <div className="column is-4">
              <div className="card">
                <div className="card-content has-text-centered">
                  <i className="fa-solid fa-microchip service-icon"></i>
                  <h3 className="title is-4 mt-4">Simulation Engine</h3>
                  <p>Monte Carlo simulations modelling game-specific mechanics â€” map vetoes, game modes, round economics.</p>
                </div>
              </div>
            </div>
            <div className="column is-4">
              <div className="card">
                <div className="card-content has-text-centered">
                  <i className="fa-solid fa-plug service-icon"></i>
                  <h3 className="title is-4 mt-4">API Integration</h3>
                  <p>RESTful API with event-driven distribution via RabbitMQ. Plug into your existing sportsbook platform.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="parallax-divider parallax-divider-2"></section>

      <section id="contact" className="section contact-section">
        <div className="container">
          <h2 className="title is-2 has-text-centered has-text-white animate-on-scroll">Get In Touch</h2>
          <p className="has-text-centered is-size-5 has-text-grey-light mb-6 animate-on-scroll delay-1">
            Ready to add simulation-powered esports pricing to your sportsbook?
          </p>
          <div className={"contact-form-wrapper" + (sending || sent ? " is-sent" : "")}>
            <div className="columns is-centered mt-5">
              <div className="column is-8">
                <form ref={formRef} onSubmit={handleSubmit} className="columns is-multiline animate-on-scroll delay-2">
                  <div className="column is-6">
                    <div className="field"><label className="label has-text-white">Name</label><div className="control"><input className="input contact-input" type="text" name="name" placeholder="Your name" required /></div></div>
                  </div>
                  <div className="column is-6">
                    <div className="field"><label className="label has-text-white">Company</label><div className="control"><input className="input contact-input" type="text" name="company" placeholder="Your company" required /></div></div>
                  </div>
                  <div className="column is-6">
                    <div className="field"><label className="label has-text-white">Email</label><div className="control"><input className="input contact-input" type="email" name="email" placeholder="your@email.com" required /></div></div>
                  </div>
                  <div className="column is-6">
                    <div className="field"><label className="label has-text-white">Role</label><div className="control"><div className="select is-fullwidth contact-select"><select name="role"><option>Sportsbook Operator</option><option>Trading Desk</option><option>Platform Provider</option><option>Other</option></select></div></div></div>
                  </div>
                  <div className="column is-12">
                    <div className="field"><label className="label has-text-white">Message</label><div className="control"><textarea className="textarea contact-input" name="message" placeholder="Tell us about your esports pricing needs..." required></textarea></div></div>
                  </div>
                  <div className="column is-12 has-text-centered">
                    <div ref={recaptchaRef} style={{display: 'inline-block', marginBottom: '16px'}}></div>
                    <br/>
                    <button type="submit" className="button is-primary is-medium mt-3">Request Demo</button>
                  </div>
                </form>
              </div>
            </div>
            <div className="success-message">
              {sending ? (
                <div className="sending-spinner"></div>
              ) : (
                <div className="success-checkmark">&#10004;</div>
              )}
              <h3 className="title is-4 has-text-white mt-4">{sending ? "Sending..." : "Thanks for contacting us!"}</h3>
              <p className="has-text-grey-light">{sending ? "Please wait a moment" : "We'll get back to you shortly with more information about our esports pricing solutions."}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
